import {
  Box,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Link as MuiLink,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { alpha } from '@mui/material/styles';
import { LoginFormProps } from './types';

export function LoginForm({
  email,
  password,
  showPassword,
  loading,
  error,
  success,
  onEmailChange,
  onPasswordChange,
  onTogglePasswordVisibility,
  onSubmit,
  onForgotPassword,
  onCreateAccount,
}: LoginFormProps) {
  return (
    <>
      <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={onEmailChange}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              background: (t) => t.palette.mode === 'dark'
                ? alpha(t.palette.common.white, 0.04)
                : alpha(t.palette.common.white, 0.6),
              backdropFilter: 'blur(4px)',
              '&.Mui-focused': {
                boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.15)}`,
                '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
              },
            },
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={onPasswordChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={onTogglePasswordVisibility}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              background: (t) => t.palette.mode === 'dark'
                ? alpha(t.palette.common.white, 0.04)
                : alpha(t.palette.common.white, 0.6),
              backdropFilter: 'blur(4px)',
              '&.Mui-focused': {
                boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.15)}`,
                '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
              },
            },
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={(t) => ({
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: 'subtitle1.fontSize',
            backgroundImage: `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
            color: 'primary.contrastText',
            boxShadow: [
              `0 6px 20px ${alpha(t.palette.primary.main, 0.4)}`,
              `inset 0 2px 0 ${alpha(t.palette.common.white, 0.2)}`,
              `inset 0 -2px 0 ${alpha(t.palette.common.black, 0.12)}`,
            ].join(', '),
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
              backgroundImage: `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.main})`,
              boxShadow: [
                `0 8px 28px ${alpha(t.palette.primary.main, 0.5)}`,
                `inset 0 2px 0 ${alpha(t.palette.common.white, 0.25)}`,
                `inset 0 -2px 0 ${alpha(t.palette.common.black, 0.12)}`,
              ].join(', '),
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              backgroundImage: 'none',
            },
          })}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
        <Button
          onClick={onCreateAccount}
          fullWidth
          variant="outlined"
          size="large"
          sx={(t) => ({
            mt: 2,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: 'subtitle1.fontSize',
            borderWidth: 2,
            borderColor: t.palette.primary.main,
            background: alpha(t.palette.primary.main, 0.05),
            backdropFilter: 'blur(4px)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
            '&:hover': {
              borderWidth: 2,
              background: alpha(t.palette.primary.main, 0.1),
              boxShadow: `0 4px 16px ${alpha(t.palette.primary.main, 0.2)}`,
              transform: 'translateY(-1px)',
            },
          })}
        >
          Create New Account
        </Button>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 2 }}>
        <MuiLink
          component="button"
          variant="body2"
          onClick={onForgotPassword}
          disabled={loading}
          sx={{ 
            color: 'primary.main',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Forgot Password?
        </MuiLink>
      </Box>
    </>
  );
} 